import { Box, Chip, CircularProgress, Paper, Typography } from '@mui/material';
import {
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  History as HistoryIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import type { Device } from '@/types/domain';
import { getOnlineStatus } from '../utils';
import { formatApiDateTime } from '@/features/license-keys/utils';

interface DeviceTableProps {
  devices: Device[];
  loading: boolean;
  onLock: (id: number) => void;
  onUnlock: (id: number) => void;
  onDelete: (id: number) => void;
  onViewLogs: (device: Device) => void;
}

/**
 * 在线状态颜色类型
 */
type OnlineStatusColor = 'success' | 'warning' | 'default';

/**
 * 获取在线状态对应的颜色
 */
function getStatusColor(status: string, t: (key: string) => string): OnlineStatusColor {
  const onlineStatus = t('devices.online');
  const recentlyActiveStatus = t('devices.recentlyActive');

  if (status === onlineStatus) return 'success';
  if (status === recentlyActiveStatus) return 'warning';
  return 'default';
}

/**
 * 设备列表表格组件
 */
export default function DeviceTable({
  devices,
  loading,
  onLock,
  onUnlock,
  onDelete,
  onViewLogs,
}: DeviceTableProps) {
  const { t } = useTranslation();

  const columns: GridColDef<Device>[] = [
    { field: 'id', headerName: t('devices.id'), width: 80 },
    {
      field: 'license_key',
      headerName: t('devices.licenseKey'),
      width: 180,
      renderCell: (params) => (
        <Typography variant="body2" fontFamily="monospace">
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'hardware_id',
      headerName: t('devices.hardwareId'),
      width: 200,
      renderCell: (params) => (
        <Typography
          variant="body2"
          fontFamily="monospace"
          sx={{ fontSize: '0.75rem' }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'device_name',
      headerName: t('devices.deviceName'),
      width: 200,
      valueGetter: (_value, row) => row?.device_name || t('devices.unknown'),
    },
    {
      field: 'activated_at',
      headerName: t('devices.activatedAt'),
      width: 180,
      renderCell: (params) => formatApiDateTime(params?.row?.activated_at, ''),
    },
    {
      field: 'last_heartbeat',
      headerName: t('devices.lastHeartbeat'),
      width: 180,
      renderCell: (params) =>
        formatApiDateTime(params?.row?.last_heartbeat, t('common.never')),
    },
    {
      field: 'online_status',
      headerName: t('devices.connection'),
      width: 140,
      valueGetter: (_value, row) => getOnlineStatus(row?.last_heartbeat, t),
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={getStatusColor(params.value, t) ?? 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'status',
      headerName: t('devices.status'),
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={
            params.value === 'active'
              ? 'success'
              : params.value === 'locked'
                ? 'error'
                : 'default'
          }
          size="small"
        />
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: t('devices.actions'),
      width: 180,
      getActions: (params) => {
        const row = params.row;
        return [
          <GridActionsCellItem
            key="logs"
            icon={<HistoryIcon />}
            label={t('devices.viewLogs')}
            onClick={() => row && onViewLogs(row)}
          />,
          row?.status === 'active' ? (
            <GridActionsCellItem
              key="lock"
              icon={<LockIcon />}
              label={t('devices.lock')}
              onClick={() => row?.id && onLock(row.id)}
              showInMenu={false}
            />
          ) : (
            <GridActionsCellItem
              key="unlock"
              icon={<LockOpenIcon />}
              label={t('devices.unlock')}
              onClick={() => row?.id && onUnlock(row.id)}
              showInMenu={false}
            />
          ),
          <GridActionsCellItem
            key="delete"
            icon={<DeleteIcon />}
            label={t('devices.deactivate')}
            onClick={() => row?.id && onDelete(row.id)}
            showInMenu={true}
          />,
        ];
      },
    },
  ];

  if (loading) {
    return (
      <Paper>
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  return (
    <Paper>
      <DataGrid
        rows={devices.map((d) => ({ ...d, id: d.id }))}
        columns={columns}
        initialState={{
          pagination: {
            paginationModel: { pageSize: 10 },
          },
        }}
        pageSizeOptions={[10, 25, 50]}
        disableRowSelectionOnClick
      />
    </Paper>
  );
}
